import React, { useState, useEffect } from 'react';
import { useAuthV2 } from '@/context/auth-v2/AuthContext';
import { api } from '@/services/api';
import DevStaffManagement from '@/components/dev/DevStaffManagement';
import { isDevEnvironment } from '@/utils/environment';
import { CHARITY_ROLE_PERMISSIONS, SUPER_ADMIN_PERMISSIONS } from '@/types/roles';
import type { CharityRole } from '@/types/roles';
import { toast } from 'react-hot-toast';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { createSetupIntent, updateBusinessPaymentMethod, getBusinessPaymentMethods } from '@/services/stripe';

// Initialize Stripe - in a real app, get this from environment variables
const stripePromise = loadStripe('pk_test_51NxSxXFCENsRZk5QQsHxdGLiJ06mjrQeZfLJQCOdpkRgAoG4QHc1m8W7wgLe7EtRHGMLyeFEp6JWxbpHYMxprDiP003LTWRnN5');

const stripePromiseForComponent = stripePromise;

const CharitySettings: React.FC = () => {
  const { user, updateUser } = useAuthV2();
  // If user is a super admin, treat them as an admin, otherwise use their assigned role or default to volunteer
  const isSuperAdmin = user?.role === 'super_admin';
  const userRole = isSuperAdmin ? 'admin' as CharityRole : (user?.role as CharityRole || 'volunteer');
  const permissions = isSuperAdmin ? SUPER_ADMIN_PERMISSIONS : CHARITY_ROLE_PERMISSIONS[userRole];
  
  // Check if we're in development mode to enable full access to features
  const isDevelopment = isDevEnvironment();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // Payment-related state
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [clientSecret, setClientSecret] = useState('');

  // Define charity data type based on business model with charity-specific role
  interface CharityData {
    business_id: string;
    business_name: string;
    role: string; // 'charity' or similar designation
    description: string; // Used for mission statement
    license_number?: string;
    email: string;
    phone_number?: string;
    address?: {
      street: string;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    };
    location?: {
      latitude: number;
      longitude: number;
    };
    registered_status?: string;
    created_at?: string;
    website_url?: string;
    social_media?: {
      facebook?: string;
      twitter?: string;
      instagram?: string;
    };
  }
  
  // State for user and charity data
  const [userData, setUserData] = useState({
    name: user?.name || '',
    profilePicture: user?.profilePicture || '',
    email: user?.email || ''
  });

  const [charityData, setCharityData] = useState<CharityData | null>(null);

  // Fetch charity data when component mounts
  useEffect(() => {
    if (user?.business_id) {
      // For charity users, the business_id field is used for the charity ID
      fetchCharityData(user.business_id);
      fetchPaymentMethods(user.business_id);
    } else {
      setIsLoading(false);
    }
  }, [user]);
  
  // Function to fetch payment methods
  const fetchPaymentMethods = async (businessId: string) => {
    if (!businessId) return;
    
    try {
      setLoadingPaymentMethods(true);
      const methods = await getBusinessPaymentMethods(businessId);
      setPaymentMethods(methods || []);
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      toast.error('Failed to load payment methods');
    } finally {
      setLoadingPaymentMethods(false);
    }
  };
  
  // Function to handle payment method updates

  const fetchCharityData = async (businessId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<CharityData>(`/api/business/${businessId}`);
      setCharityData(response.data);
    } catch (err) {
      console.error('Error fetching charity data:', err);
      setError('Failed to load charity information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCharityChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Handle nested properties (address.street, location.latitude, etc.)
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setCharityData(prev => {
        if (!prev) return prev;
        
        return {
          ...prev,
          [parent]: {
            ...((prev[parent as keyof typeof prev] as any) || {}),
            [child]: value
          }
        };
      });
    } else {
      // Handle top-level properties
      setCharityData(prev => {
        if (!prev) return prev;
        return { ...prev, [name]: value };
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSaving(true);
    setError(null);

    try {
      // Update user data
      await updateUser(userData);

      // Update charity data if available
      if (charityData && user.business_id) {
        try {
          // Create a properly typed payload that handles optional fields
          const charityUpdatePayload: Record<string, any> = {
            business_name: charityData.business_name,
            description: charityData.description,
            role: charityData.role
          };
          
          // Only add optional fields if they're defined
          if (charityData.license_number) {
            charityUpdatePayload.license_number = charityData.license_number;
          }
          
          if (charityData.email) {
            charityUpdatePayload.email = charityData.email;
          }
          
          if (charityData.phone_number) {
            charityUpdatePayload.phone_number = charityData.phone_number;
          }
          
          if (charityData.address) {
            charityUpdatePayload.address = charityData.address;
          }
          
          if (charityData.location) {
            charityUpdatePayload.location = charityData.location;
          }
          
          if (charityData.registered_status) {
            charityUpdatePayload.registered_status = charityData.registered_status;
          }
          
          if (charityData.website_url) {
            charityUpdatePayload.website_url = charityData.website_url;
          }
          
          if (charityData.social_media) {
            charityUpdatePayload.social_media = charityData.social_media;
          }
          
          const response = await api.put(`/api/business/${user.business_id}`, charityUpdatePayload);
          
          if (response.status >= 200 && response.status < 300) {
            toast.success('Charity information updated successfully!');
          }
        } catch (err) {
          console.error('Error updating charity details:', err);
          toast.error('Failed to update charity details');
        }
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center p-8">Loading...</div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          <div className="flex items-center">
            <h1 className="text-xl font-medium text-navy-blue">Charity Settings</h1>
          </div>
          <div>
            <p className="text-sm text-gray-500">
              Update your profile and charity information.
            </p>
          </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        )}

      <form onSubmit={handleSubmit}>
        <div className="space-y-8 divide-y divide-gray-200">
          {/* User Profile Section */}
          <div className="space-y-8 divide-y divide-gray-200 sm:space-y-5">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">User Profile</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Your personal information.
              </p>
            </div>

            <div className="space-y-6 sm:space-y-5 pt-6">
              <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                  Name
                </label>
                <div className="mt-1 sm:mt-0 sm:col-span-2">
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={userData.name}
                    onChange={handleUserChange}
                    className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                  Email
                </label>
                <div className="mt-1 sm:mt-0 sm:col-span-2">
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={userData.email}
                    onChange={handleUserChange}
                    className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                <label htmlFor="profilePicture" className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                  Profile Picture URL
                </label>
                <div className="mt-1 sm:mt-0 sm:col-span-2">
                  <input
                    type="text"
                    name="profilePicture"
                    id="profilePicture"
                    value={userData.profilePicture}
                    onChange={handleUserChange}
                    className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Charity Information Section - Only show if charity data is available */}
          {charityData && permissions.canModifyBusinessDetails && (
            <div className="space-y-8 divide-y divide-gray-200 sm:space-y-5">
              <div className="pt-8 space-y-6 sm:pt-10 sm:space-y-5">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Charity Information</h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    Update your charity details
                  </p>
                </div>

                <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
                  <label htmlFor="business_name" className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                    Charity Name
                  </label>
                  <div className="mt-1 sm:mt-0 sm:col-span-2">
                    <input
                      type="text"
                      name="business_name"
                      id="business_name"
                      value={charityData.business_name || ''}
                      onChange={handleCharityChange}
                      className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                    Mission Statement
                  </label>
                  <div className="mt-1 sm:mt-0 sm:col-span-2">
                    <textarea
                      id="description"
                      name="description"
                      rows={3}
                      value={charityData.description || ''}
                      onChange={handleCharityChange}
                      className="max-w-lg shadow-sm block w-full focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                    Charity Type
                  </label>
                  <div className="mt-1 sm:mt-0 sm:col-span-2">
                    <select
                      name="role"
                      id="role"
                      value={charityData.role || ''}
                      onChange={handleCharityChange}
                      className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                    >
                      <option value="charity">Charity</option>
                      <option value="non-profit">Non-Profit</option>
                      <option value="foundation">Foundation</option>
                    </select>
                  </div>
                </div>

                <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                    Email
                  </label>
                  <div className="mt-1 sm:mt-0 sm:col-span-2">
                    <input
                      type="email"
                      name="email"
                      id="charity_email"
                      value={charityData.email || ''}
                      onChange={handleCharityChange}
                      className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                  <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                    Phone
                  </label>
                  <div className="mt-1 sm:mt-0 sm:col-span-2">
                    <input
                      type="text"
                      name="phone_number"
                      id="phone_number"
                      value={charityData.phone_number || ''}
                      onChange={handleCharityChange}
                      className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                  <label htmlFor="website_url" className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                    Website
                  </label>
                  <div className="mt-1 sm:mt-0 sm:col-span-2">
                    <input
                      type="url"
                      name="website_url"
                      id="website_url"
                      value={charityData.website_url || ''}
                      onChange={handleCharityChange}
                      className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-5">
                  <h4 className="text-md font-medium mb-2">Address Information</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Street</label>
                      <input
                        id="address.street"
                        name="address.street"
                        type="text"
                        value={charityData?.address?.street || ''}
                        onChange={handleCharityChange}
                        className="mt-1 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">City</label>
                      <input
                        id="address.city"
                        name="address.city"
                        type="text"
                        value={charityData?.address?.city || ''}
                        onChange={handleCharityChange}
                        className="mt-1 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">State</label>
                      <input
                        id="address.state"
                        name="address.state"
                        type="text"
                        value={charityData?.address?.state || ''}
                        onChange={handleCharityChange}
                        className="mt-1 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                      <input
                        id="address.postal_code"
                        name="address.postal_code"
                        type="text"
                        value={charityData?.address?.postal_code || ''}
                        onChange={handleCharityChange}
                        className="mt-1 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Country</label>
                      <input
                        id="address.country"
                        name="address.country"
                        type="text"
                        value={charityData?.address?.country || 'United States'}
                        onChange={handleCharityChange}
                        className="mt-1 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-5">
                  <h4 className="text-md font-medium mb-2">Location Information</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Latitude</label>
                      <input
                        id="location.latitude"
                        name="location.latitude"
                        type="number"
                        step="any"
                        value={charityData?.location?.latitude || ''}
                        onChange={handleCharityChange}
                        className="mt-1 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Longitude</label>
                      <input
                        id="location.longitude"
                        name="location.longitude"
                        type="number"
                        step="any"
                        value={charityData?.location?.longitude || ''}
                        onChange={handleCharityChange}
                        className="mt-1 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>

                <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                  <label htmlFor="business_id" className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                    Charity ID
                  </label>
                  <div className="mt-1 sm:mt-0 sm:col-span-2">
                    <input
                      type="text"
                      id="business_id"
                      name="business_id"
                      value={charityData.business_id}
                      disabled
                      className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 bg-gray-100 rounded-md"
                    />
                  </div>
                </div>
                
                <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                  <label htmlFor="registered_status" className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                    Registration Status
                  </label>
                  <div className="mt-1 sm:mt-0 sm:col-span-2">
                    <select
                      name="registered_status"
                      id="registered_status"
                      value={charityData.registered_status || ''}
                      onChange={handleCharityChange}
                      className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                </div>
                
                <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
                  <label htmlFor="created_at" className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                    Created At
                  </label>
                  <div className="mt-1 sm:mt-0 sm:col-span-2">
                    <input
                      type="text"
                      id="created_at"
                      name="created_at"
                      value={charityData.created_at ? new Date(charityData.created_at).toLocaleString() : 'N/A'}
                      disabled
                      className="max-w-lg block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 bg-gray-100 rounded-md"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="pt-5">
            <div className="flex justify-end">
              <button
                type="button"
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Staff Management Section */}
      {(permissions.canManageStaff || isDevelopment) && (
        <div className="mt-8 bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Staff Management</h2>
          <DevStaffManagement orgType="charity" />
        </div>
      )}
      
      {/* Payment Methods Section */}
      {(permissions.canModifyBusinessDetails || userRole === 'owner' || userRole === 'manager') && (
        <div className="bg-white p-6 rounded-lg shadow mt-8">
          <h2 className="text-xl font-semibold mb-4">Payment Methods</h2>
          <div className="space-y-4">
            <p className="text-gray-600">
              Manage your charity payment methods and billing information.
            </p>

            {loadingPaymentMethods ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-navy-blue"></div>
              </div>
            ) : paymentMethods.length > 0 ? (
              <div className="space-y-4">
                {paymentMethods.map((method: any) => (
                  <div key={method.id} className="border p-4 rounded">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">{method.brand?.toUpperCase() || 'CARD'} •••• {method.last4 || '****'}</p>
                        <p className="text-sm text-gray-500">Expires {method.exp_month || 'MM'}/{method.exp_year || 'YYYY'}</p>
                      </div>
                      {method.is_default && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Default</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 mb-4">No payment methods found.</p>
            )}
            
            <button
              type="button"
              onClick={async () => {
                if (user?.business_id) {
                  try {
                    // Create a setup intent to securely collect payment details
                    const setupIntent = await createSetupIntent();
                    setClientSecret(setupIntent.client_secret);
                    setShowPaymentForm(true);
                  } catch (err) {
                    console.error('Error setting up payment method:', err);
                    toast.error('Failed to initialize payment form');
                  }
                } else {
                  toast.error('Charity ID is required to add payment methods');
                }
              }}
              className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              disabled={showPaymentForm}
            >
              {paymentMethods.length > 0 ? 'Update Payment Method' : 'Add Payment Method'}
            </button>
          </div>
          
          {showPaymentForm && clientSecret && user?.business_id && (
            <div className="mt-6 border p-6 rounded bg-gray-50">
              <Elements stripe={stripePromiseForComponent}>
                <PaymentMethodForm 
                  clientSecret={clientSecret} 
                  charityId={user.business_id}
                  onSuccess={() => {
                    setShowPaymentForm(false);
                    if (user.business_id) {
                      fetchPaymentMethods(user.business_id);
                    }
                    toast.success('Payment method updated successfully!');
                  }}
                  onCancel={() => setShowPaymentForm(false)}
                />
              </Elements>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
    </div>
  );
};

// Payment Method Form Component
const PaymentMethodForm = ({ 
  clientSecret, 
  charityId,
  onSuccess,
  onCancel 
}: { 
  clientSecret: string; 
  charityId: string;
  onSuccess: () => void;
  onCancel: () => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return;
    }
    
    setProcessing(true);
    setError(null);
    
    try {
      // Use the card Element to create a payment method
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement)!,
          billing_details: {
            // You can collect name, email, etc. here if needed
          },
        },
      });
      
      if (result.error) {
        // Show error to your customer
        setError(result.error.message || 'An error occurred while processing your card');
      } else {
        // The setup has succeeded
        if (result.setupIntent && result.setupIntent.payment_method) {
          // Update the charity's payment method in your backend
          // Call the imported service function
          await updateBusinessPaymentMethod({
            business_id: charityId,
            payment_method_id: result.setupIntent.payment_method as string
          });
          
          onSuccess();
        }
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while updating your payment method');
    } finally {
      setProcessing(false);
    }
  };
  
  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <h3 className="text-lg font-medium mb-4">Enter Card Details</h3>
      
      <div className="mb-4 p-3 border rounded bg-white">
        <CardElement options={cardElementOptions} />
      </div>
      
      {error && (
        <div className="text-red-500 text-sm mb-4">{error}</div>
      )}
      
      <div className="flex space-x-4">
        <button 
          type="submit" 
          className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
          disabled={!stripe || processing}
        >
          {processing ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Processing...
            </div>
          ) : 'Save Card'}
        </button>
        
        <button 
          type="button" 
          className="border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
          onClick={onCancel}
          disabled={processing}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default CharitySettings;